import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, ExternalLink, Clock, Instagram, Facebook, Youtube, AtSign } from 'lucide-react';
import WhatsAppIcon from 'components/ui/WhatsAppIcon';
import SectionHeading from 'components/ui/SectionHeading';
import Button from 'components/ui/Button';
import PageMeta from 'components/layout/PageMeta';
import { BRAND, SOCIAL_LINKS } from 'lib/constants';
import { fadeUpVariant, staggerContainer, staggerItem } from 'lib/utils';
import { buildContactMessage, getWhatsAppUrl } from 'lib/whatsapp';
import heroBg from '../images/marketing/emotional-bg.webp';

export const metadata = {
  title: 'Contact Us | Singaraa',
  description: 'Get in touch with the Singaraa team. We would love to hear from you. Order online, inquire about partnerships, or share your feedback.',
};

const iconMap = {
  Instagram,
  Facebook,
  Youtube,
  AtSign,
  Mail,
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Mobile Number is required';
    } else if (formData.phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = 'Valid 10-digit number required';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const message = buildContactMessage(formData);
    const whatsappUrl = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const inputClasses =
    'w-full px-4 py-3 rounded-xl border border-singaraa-caramel/25 focus:border-singaraa-caramel focus:ring-2 focus:ring-singaraa-caramel/20 focus:outline-none bg-[#Fdfbf7] font-body text-singaraa-dark placeholder:text-singaraa-dark/40 transition-colors duration-300';

  return (
    <main className="min-h-screen bg-[#Fdfbf7]">
      <PageMeta title={metadata.title} description={metadata.description} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden bg-singaraa-dark">
        <div className="absolute inset-0 z-0">
          <img
            src={heroBg}
            alt="Singaraa Contact"
            className="w-full h-full object-cover object-center opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-singaraa-dark via-singaraa-dark/80 to-transparent" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-20 relative z-10 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl mx-auto"
          >
            <motion.span
              variants={staggerItem}
              className="inline-block uppercase tracking-widest text-singaraa-gold font-semibold text-sm mb-4"
            >
              WE'RE HERE FOR YOU
            </motion.span>
            <motion.h1
              variants={fadeUpVariant}
              className="font-heading text-4xl sm:text-5xl md:text-6xl text-white font-bold mb-6"
            >
              Let's Connect.
            </motion.h1>
            <motion.p
              variants={fadeUpVariant}
              className="font-body text-singaraa-cream/80 text-lg sm:text-xl leading-relaxed"
            >
              Whether you have a question about your order, want to share feedback, or just want to say hello - our team is ready to help.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Contact Grid Section */}
      <section className="py-16 sm:py-24 bg-singaraa-ivory relative z-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto -mt-24 sm:-mt-32 mb-16">

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="bg-white rounded-3xl p-8 sm:p-10 shadow-xl shadow-singaraa-caramel/10 border border-singaraa-caramel/20 flex flex-col h-full"
            >
              <div className="w-14 h-14 rounded-2xl bg-[#25D366]/10 text-[#25D366] flex items-center justify-center mb-6">
                <WhatsAppIcon className="w-7 h-7" />
              </div>
              <h3 className="font-heading text-xl font-bold text-singaraa-dark mb-3">WhatsApp Us</h3>
              <p className="text-singaraa-dark/70 font-body text-sm mb-6 flex-grow">
                The fastest way to reach us! We are available for instant ordering and support via WhatsApp.
              </p>
              <Button
                variant="whatsapp"
                href={`https://wa.me/${BRAND.whatsapp}?text=Hello%20Singaraa%20Team!`}
                target="_blank"
                className="w-full justify-center"
              >
                Message on WhatsApp
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="bg-white rounded-3xl p-8 sm:p-10 shadow-xl shadow-singaraa-caramel/10 border border-singaraa-caramel/20 flex flex-col h-full"
            >
              <div className="w-14 h-14 rounded-2xl bg-singaraa-brown/10 text-singaraa-brown flex items-center justify-center mb-6">
                <Phone className="w-7 h-7" />
              </div>
              <h3 className="font-heading text-xl font-bold text-singaraa-dark mb-3">Call Us</h3>
              <p className="text-singaraa-dark/70 font-body text-sm mb-6 flex-grow">
                Have questions or need help? Give us a call directly. We're happy to help.
              </p>
              <a
                href={`tel:${BRAND.phone}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-singaraa-cream text-singaraa-brown font-semibold hover:bg-singaraa-beige transition-colors w-full"
              >
                {BRAND.phoneDisplay}
              </a>
              <div className="flex items-center gap-2 mt-4 text-xs text-singaraa-dark/50 font-medium justify-center">
                <Clock className="w-4 h-4" />
                <span>Mon-Sat, 9:00 AM - 7:00 PM</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.75, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="bg-white rounded-3xl p-8 sm:p-10 shadow-xl shadow-singaraa-caramel/10 border border-singaraa-caramel/20 flex flex-col h-full md:col-span-2 lg:col-span-1"
            >
              <div className="w-14 h-14 rounded-2xl bg-singaraa-gold/10 text-singaraa-gold flex items-center justify-center mb-6">
                <Mail className="w-7 h-7" />
              </div>
              <h3 className="font-heading text-xl font-bold text-singaraa-dark mb-3">Email Us</h3>
              <p className="text-singaraa-dark/70 font-body text-sm mb-6 flex-grow">
                For business inquiries, feedback, or detailed questions, drop us an email anytime.
              </p>
              <a
                href={`mailto:${BRAND.email}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-singaraa-cream text-singaraa-brown font-semibold hover:bg-singaraa-beige transition-colors w-full break-all"
              >
                {BRAND.email}
              </a>
            </motion.div>
          </div>

          {/* Contact Form Section */}
          <div className="max-w-3xl mx-auto bg-white rounded-3xl p-6 sm:p-10 shadow-2xl shadow-singaraa-dark/5 border border-singaraa-caramel/15 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-singaraa-caramel/5 rounded-full blur-3xl pointer-events-none" />
            <SectionHeading
              eyebrow="SEND A MESSAGE"
              title="Drop Us a Line"
              subtitle="Fill out the form below and we will get back to you directly via WhatsApp."
              className="mb-8"
            />

            <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-semibold text-singaraa-dark uppercase tracking-wider mb-2">Your Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your name..."
                    className={inputClasses}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1 font-body">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-singaraa-dark uppercase tracking-wider mb-2">Mobile Number (WhatsApp) *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your WhatsApp number..."
                    maxLength={14}
                    className={inputClasses}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1 font-body">{errors.phone}</p>}
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-singaraa-dark uppercase tracking-wider mb-2">Email Address (Optional)</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address..."
                  className={inputClasses}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-singaraa-dark uppercase tracking-wider mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Type your message here..."
                  className={`${inputClasses} resize-none`}
                />
                {errors.message && <p className="text-red-500 text-xs mt-1 font-body">{errors.message}</p>}
              </div>

              <div className="pt-2">
                <Button type="submit" variant="whatsapp" size="lg" className="w-full sm:w-auto px-10">
                  Send Inquiry on WhatsApp
                </Button>
              </div>
            </form>
          </div>

        </div>
      </section>

      {/* Location Section */}
      <section className="py-16 sm:py-24 bg-white border-t border-singaraa-caramel/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-20 max-w-5xl">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
            <div className="flex-1 w-full text-center lg:text-left">
              <SectionHeading
                eyebrow="OUR LOCATION"
                title="Where We Roast"
                subtitle="Rooted in Gujarat, delivering crunch across India. We pack every order fresh from our facility in Ahmedabad."
                align="left"
                className="mb-8"
              />
              <div className="bg-singaraa-cream/30 p-6 sm:p-8 rounded-2xl border border-singaraa-caramel/20 inline-block text-left w-full max-w-lg mx-auto lg:mx-0">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-singaraa-brown/10 text-singaraa-brown flex flex-shrink-0 items-center justify-center mt-1">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-singaraa-dark text-lg mb-2">Singaraa Roastery</h4>
                    <p className="text-singaraa-dark/80 font-body text-sm sm:text-base leading-relaxed mb-4">
                      {BRAND.address}
                    </p>
                    <a
                      href={BRAND.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-singaraa-brown font-semibold hover:text-singaraa-caramel transition-colors"
                    >
                      View on Google Maps
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1 w-full">
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-singaraa-dark/10 border-4 border-white">
                <iframe
                  title="Singaraa Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d649.320147903736!2d72.50012083898847!3d22.98300284319999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e9beb82e3af7b%3A0xcd2a9d45c3756e90!2sCity%20Survey%20Superintendent%20Sarkhej!5e0!3m2!1sen!2sin!4v1790147772542!5m2!1sen!2sin"
                  width="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                  className="w-full h-[300px] sm:h-[400px] lg:h-[450px] grayscale opacity-90 hover:grayscale-0 transition-all duration-700"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
}

